"use client";

import { RefObject, useEffect } from "react";

/**
 * iOS Safari refuses muted autoplay in Low Power Mode or when the user has set
 * "Never Auto-Play", so `play()` rejects and the video sits on its poster.
 * A `play()` call made inside a user gesture is still allowed, and once it
 * succeeds WebKit lets later programmatic `play()` calls through for that
 * element. So on the first tap/click anywhere, start the video if it's paused
 * and on screen, then stop listening.
 */
export function useAutoplayUnlock(
  videoRef: RefObject<HTMLVideoElement | null>,
  deps: unknown[] = [],
) {
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const events = ["touchend", "click"] as const;
    const cleanup = () =>
      events.forEach((e) => document.removeEventListener(e, unlock));

    function unlock() {
      if (!video) return cleanup();
      if (!video.paused) return cleanup();
      const rect = video.getBoundingClientRect();
      const onScreen = rect.bottom > 0 && rect.top < window.innerHeight;
      if (!onScreen) return;
      video.play().then(cleanup).catch(() => {});
    }

    events.forEach((e) =>
      document.addEventListener(e, unlock, { passive: true }),
    );
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
